import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircle,
  RefreshCw,
  AlertCircle,
  ExternalLink,
} from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
  showToast,
} from "@/components/ui";
import {
  useAmazonAdminAuthSessionQuery,
  useAmazonSystemCredentialQuery,
} from "@/graphql/generated";

function openCenteredPopup(url: string, name: string, w = 600, h = 700) {
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  return window.open(
    url,
    name,
    `width=${w},height=${h},left=${left},top=${top},popup=1`,
  );
}

function formatDate(val: string | null | undefined): string {
  if (!val) return "—";
  return new Date(val).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Settings() {
  const queryClient = useQueryClient();
  const popupRef = useRef<Window | null>(null);
  const [isAuthorizing, setIsAuthorizing] = useState(false);

  const { data: credData, isLoading: credLoading } =
    useAmazonSystemCredentialQuery();
  const { refetch: fetchAuthSession, isFetching: sessionFetching } =
    useAmazonAdminAuthSessionQuery(undefined, { enabled: false });

  const credential = credData?.amazonSystemCredential;

  const handleAuthorize = async () => {
    setIsAuthorizing(true);
    try {
      const result = await fetchAuthSession();
      const url = result.data?.amazonAdminAuthSession?.url;
      if (!url) {
        showToast.error("Failed to generate auth URL", "Please try again.");
        setIsAuthorizing(false);
        return;
      }

      const popup = openCenteredPopup(url, "amazon-admin-auth");
      popupRef.current = popup;

      const onMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.status === "success") {
          showToast.success(
            "Amazon authorized",
            "SmartStore's Amazon account is now connected.",
          );
          queryClient.invalidateQueries({
            queryKey: useAmazonSystemCredentialQuery.getKey(),
          });
        } else if (event.data?.status === "error") {
          showToast.error(
            "Authorization failed",
            event.data.code ?? "Unknown error",
          );
        }
        window.removeEventListener("message", onMessage);
        setIsAuthorizing(false);
        popupRef.current = null;
      };

      window.addEventListener("message", onMessage);

      // Clean up listener if popup is closed manually
      const pollClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollClosed);
          window.removeEventListener("message", onMessage);
          setIsAuthorizing(false);
          popupRef.current = null;
        }
      }, 500);
    } catch {
      showToast.error("Failed to start authorization", "Please try again.");
      setIsAuthorizing(false);
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      popupRef.current?.close();
    };
  }, []);

  const isConfigured = credential?.configured === true;

  return (
    <DashboardLayout activePath="/settings">
      <PageHeader
        title="Settings"
        subtitle="Platform configuration and integrations"
        breadcrumbs={[{ label: "Settings", active: true }]}
      />

      <div className="max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <img
                src="/brands/amazon.svg"
                alt="Amazon"
                className="w-5 h-5 object-contain"
              />
              Amazon SP-API
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              SmartStore's internal Amazon account used to fetch product type
              schemas. This token is not seller-specific.
            </p>

            {credLoading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Loading…
              </div>
            ) : isConfigured ? (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 p-4 space-y-2">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Connected</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
                  <span className="text-slate-400 dark:text-slate-500">
                    Authorized by
                  </span>
                  <span>{credential.authorizedBy}</span>
                  <span className="text-slate-400 dark:text-slate-500">
                    Authorized at
                  </span>
                  <span>{formatDate(credential.authorizedAt)}</span>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 p-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Not configured. Authorize SmartStore's Amazon account to
                  enable product type sync.
                </p>
              </div>
            )}

            <Button
              onClick={handleAuthorize}
              disabled={isAuthorizing || sessionFetching}
              variant={isConfigured ? "secondary" : "default"}
            >
              {isAuthorizing || sessionFetching ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4" />
              )}
              {isConfigured
                ? "Re-authorize Amazon Account"
                : "Authorize Amazon Account"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
